import React from 'react';
import { ISO_COUNTRIES } from '../../constants/isoCountries';
import type { LinkedCuratorProfile } from '../../utils/accountCuratorOffer';
import type { CuratorOffer } from '../../utils/curatorOffer';

interface CuratorOfferSummaryProps {
  offer: CuratorOffer;
  linkedProfiles: LinkedCuratorProfile[];
  activeProfileUsername?: string;
  showActiveProfileOnly?: boolean;
}

const CuratorOfferSummary: React.FC<CuratorOfferSummaryProps> = ({
  offer,
  linkedProfiles,
  activeProfileUsername,
  showActiveProfileOnly = false,
}) => {
  const playlistCount =
    offer.submissionPlaylists?.length || offer.submissionPlaylistIds?.length || 0;
  const countryLabel = offer.audienceCountriesUnknown
    ? "I don't know"
    : (offer.audienceCountryCodes || [])
        .map(code => ISO_COUNTRIES.find(country => country.code === code)?.name || code)
        .join(', ') || '-';

  const openProfiles = linkedProfiles.filter(profile => profile.acceptCampaigns);
  const profileLabel = showActiveProfileOnly
    ? activeProfileUsername || openProfiles[0]?.username || 'This profile'
    : openProfiles.length > 1
      ? `${openProfiles.length} Spotify profiles open`
      : openProfiles[0]?.username || activeProfileUsername || '1 Spotify profile';

  return (
    <div className="offer-summary">
      <div className="offer-summary__row">
        <span className="offer-summary__label">Profiles:</span>
        <span className="offer-summary__value">{profileLabel}</span>
      </div>
      <div className="offer-summary__row">
        <span className="offer-summary__label">Daily streams:</span>
        <span className="offer-summary__value">
          {offer.dailyStreamsMin?.toLocaleString?.() || '-'} -{' '}
          {offer.dailyStreamsMax?.toLocaleString?.() || '-'}
        </span>
      </div>
      <div className="offer-summary__row">
        <span className="offer-summary__label">Default rate:</span>
        <span className="offer-summary__value">
          {offer.defaultRatePer1k != null
            ? `$${Number(offer.defaultRatePer1k).toFixed(2)} / 1K`
            : '-'}
        </span>
      </div>
      <div className="offer-summary__row">
        <span className="offer-summary__label">Genres:</span>
        <span className="offer-summary__value">
          {(offer.genres || []).slice(0, 4).join(', ') || '-'}
          {(offer.genres || []).length > 4 ? ` +${(offer.genres || []).length - 4}` : ''}
        </span>
      </div>
      <div className="offer-summary__row">
        <span className="offer-summary__label">Playlists:</span>
        <span className="offer-summary__value">
          {offer.includeAllOwnedPlaylists
            ? `All owned (${playlistCount})`
            : `${playlistCount} selected`}
        </span>
      </div>
      <div className="offer-summary__row">
        <span className="offer-summary__label">Feedback:</span>
        <span className="offer-summary__value">
          {typeof offer.interestedInFeedback === 'boolean'
            ? offer.interestedInFeedback
              ? 'Yes'
              : 'No'
            : '-'}
        </span>
      </div>
      <div className="offer-summary__row">
        <span className="offer-summary__label">Audience:</span>
        <span className="offer-summary__value">{countryLabel}</span>
      </div>
      {offer.notes ? (
        <div className="offer-summary__row">
          <span className="offer-summary__label">Notes:</span>
          <span className="offer-summary__value">{offer.notes}</span>
        </div>
      ) : null}
    </div>
  );
};

export default CuratorOfferSummary;
